import { createRouter, createWebHistory } from 'vue-router'

import { logError } from '@shared/helpers/Logger'
import { getNextRoute } from '@dvp/helpers/Route'

import { events$ } from '@shared/services'
import basketStore from '@shared/store/basket'
import sessionStore from '@shared/store/session'
import useApplication from '@shared/hooks/useApplication'
import useI18n from '@shared/hooks/useI18n'
import useSession from '@shared/hooks/useSession'

import applicationRoutes from '@shared/modules/Dvp/routes/application'
import basketRoutes from '@shared/modules/Dvp/routes/basket'
import digitalSignatureRoutes from '@shared/modules/Dvp/routes/digitalSignature'
import entryRoutes from '@shared/modules/Dvp/routes/entry'
import nextStepsRoutes from '@shared/modules/Dvp/routes/nextSteps'
import onboardingRoutes from '@shared/modules/Dvp/routes/onboarding'
import partnerRoutes from '@shared/modules/Dvp/routes/partner'
import personRoutes from '@shared/modules/Dvp/routes/person'
import productRoutes from '@shared/modules/Dvp/routes/product'
import quizRoutes from '@shared/modules/Dvp/routes/quiz'
import submitRoutes from '@shared/modules/Dvp/routes/submit'

import { EVENT_TRACKING } from '@shared/config/events'
import { DEFAULT_FOCUS_DELAY, PACKAGE, ROUTE_NAME } from '@shared/config/constants'

// HOOKS
const { changeLanguage } = useApplication()
const { selectedLanguage } = useI18n()

/**
 * Load all route-components
 */
function getRoutes() {
  return [
    {
      name: 'root',
      path: '/',
      strict: true,
      redirect: function (to) {
        return {
          name: getNextRoute(basketStore.basket),
          replace: true,
          params: {
            lang: selectedLanguage.value.toLowerCase(),
          },
          query: {
            ...to.query,
          },
        }
      },
    },
    {
      component: () => import('@shared/modules/Dvp/components/GlobalError'),
      path: '/error',
      name: ROUTE_NAME.GLOBAL_ERROR,
    },
    {
      beforeEnter: function (to) {
        // @note: only language (e.g. '/de') is present, no subpath
        if (to.path.split('/').filter(i => i.trim()).length === 1 && to.params.lang) {
          return {
            name: getNextRoute(basketStore.basket),
            replace: true,
            params: {
              ...to.params,
            },
            query: {
              ...to.query,
            },
          }
        } else {
          return
        }
      },
      path: '/:lang',
      component: () => import('@shared/modules/Dvp/components/Dvp'),
      children: [
        ...entryRoutes,
        ...personRoutes,
        ...productRoutes,
        ...basketRoutes,
        ...onboardingRoutes,
        ...applicationRoutes,
        ...submitRoutes,
        ...digitalSignatureRoutes,
        ...nextStepsRoutes,
        ...partnerRoutes,
        ...quizRoutes,
      ],
    },
  ]
}

const Router = createRouter({
  history: createWebHistory(import.meta.env.VITE_PATH_DVP),
  routes: getRoutes(),
  scrollBehavior(to) {
    // can be aborted via query-option on caller
    if (to.query.scrollTop === 'false') {
      return Promise.resolve()
    }

    // can be aborted via meta-option on route
    if (to.meta?.scrollTop === false) {
      return Promise.resolve()
    }

    if (window.pageYOffset > 0) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            top: 0,
            behavior: 'smooth',
          })
        }, DEFAULT_FOCUS_DELAY)
      })
    }
  },
})

Router.beforeEach(async (to, from, next) => {
  if (to.name === ROUTE_NAME.GLOBAL_ERROR) {
    next()
  }

  const { autostart } = useSession()

  const disableNavigation = !!(
    (window.OVP_APP_DISABLE_NAVIGATION || from.meta.disableNavigation) &&
    !to.query?.forcedRouting
  )

  const routeLanguage = to.params.lang.toUpperCase()
  await changeLanguage({ language: routeLanguage, additionalApps: [PACKAGE.PRODUCTS] })

  if (disableNavigation) {
    next(false)
  } else {
    const isPrivate = to.meta?.secured ?? true
    let __basket

    if (isPrivate && !sessionStore.isActive.value) {
      try {
        __basket = await autostart({ language: routeLanguage }, false)
      } catch {
        // issue with resuming/generating session => restart with fresh session
        __basket = await autostart({ language: routeLanguage }, true)
      }

      // when the basket has already been submitted, everything should route to nextSteps
      if (to.name !== ROUTE_NAME.NEXT_STEPS && __basket.submitted) {
        next({
          name: ROUTE_NAME.NEXT_STEPS,
          replace: true,
          params: {
            lang: to.params.lang,
          },
          query: {
            forcedRouting: true,
          },
        })
        return
      }
    }

    // a route can have tracking-data defined, which gets passed via event to the tracking-plugin
    if (to?.meta?.tracking) {
      events$.emit(EVENT_TRACKING.SET, to.meta.tracking)
    }

    next()
  }
})

Router.afterEach((to, from, failure) => {
  try {
    // if the routing gets aborted programmatically, skip the event/tracking
    if (!failure) {
      // we expect a sessionStorage item to be set, otherwise nothing will trigger
      events$.emit(EVENT_TRACKING.ROUTE_CHANGE, { opts: { strict: true }, to, from })
    }
  } catch (error) {
    logError(error)
  }
})

export default Router
